import React, { useState } from 'react';
import { Grid, Box, Typography, makeStyles, Checkbox, Button, Tooltip, IconButton, useTheme, CircularProgress } from '@material-ui/core';
import { PictureAsPdf, Description } from '@material-ui/icons';
import axios from 'axios';
import { REST_API_END_POINT } from 'src/constants/Defaultvalues';
import { useSnackbar } from 'notistack';
import { Icon } from '@iconify/react';
import trash2Outline from '@iconify/icons-eva/trash-2-outline';
import AttachmentDownloadIconButton from 'src/components/_dashboard/official-request/AttachmentDownloadIconButton';
import AttachmentPreviewDialog from 'src/components/_dashboard/official-request/AttachmentPreviewDialog';

const useStyles = makeStyles((theme) => ({
    attachmentBox: {
        cursor: 'pointer',
        display: 'flex',
        borderRadius: '6px',
        backgroundColor: '#172e34',
        // padding: theme.spacing(1),
        // margin: theme.spacing(1, 0),
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'relative', 
        height: '100px',
         width: '200px',
    },
    attachmentIcon: {
        marginRight: theme.spacing(1),
    },
    delete:{
        m:2,
        display:'flex',
        justifyContent:'flex-end' 
      },
    checkbox: {
        position: 'absolute',
        top: theme.spacing(1), 
        right: theme.spacing(1), 
    },
    checkboxContainer: {
        position: 'absolute',
        top: 0,
        right: 0,
    },
    dialogContent: {
        padding: theme.spacing(3),
        maxWidth: 768,
        margin: 'auto',
        display: 'flex',
        flexDirection:'column',
        justifyContent: 'center',
        alignItems: 'center',
    },
    gridContainer: {
        height: 'auto',
    },
}));

const MEDIA_EXTENSIONS = new Set(['image', 'img', 'video', 'other']);

/** Extension from URL/name; ID is often a numeric PK, not a filename. */
const getAttachmentFileExtension = (attachment) => {
    const rawExt = attachment?.extension;
    if (rawExt && !MEDIA_EXTENSIONS.has(String(rawExt).toLowerCase())) {
        return String(rawExt).toLowerCase().replace(/^\./, '');
    }
    const source = attachment?.file_url || attachment?.name;
    if (!source) return '';
    const pathPart = String(source).split('/').pop()?.split('?')[0] || '';
    if (!pathPart.includes('.')) return '';
    return pathPart.split('.').pop()?.toLowerCase() || '';
};

export default function OtherFiles({data,refresh,setRefresh,projectId,taskId,processing,canManage = true}) {
    const classes = useStyles();
    let user = JSON.parse(localStorage.getItem('user'))
    const isClient = user?.isClient
    const userType = isClient ? 3 : user?.user_type
    const canDownloadFiles = !isClient && userType !== 3
    const {enqueueSnackbar} = useSnackbar()
    const [open, setOpen] = useState(false);
    const [selectedAttachment, setSelectedAttachment] = useState(null);
    const [pdfLoading, setPdfLoading] = useState(true);
    const [useOfficeViewer, setUseOfficeViewer] = useState(true);
    const [checkedItems, setCheckedItems] = useState([]);
          const theme = useTheme()

    const attachmentToFile = (attachment) =>
        attachment
            ? {
                  ...attachment,
                  url: attachment.file_url || attachment.url || attachment.name,
                  file_type: attachment.file_type,
                  name: attachment.name,
              }
            : null
    

    const handleCheckboxChange = (id) => {
        setCheckedItems((prevState) => {
            const isPresent = prevState.includes(id);
            const updatedState = isPresent
              ? prevState.filter((item) => item !== id)
              : [...prevState, id];
            return updatedState;
          });   
    };
     
    const handleDelete=async()=>{
        try {
            if (!canManage) {
                enqueueSnackbar("Access Denied: You do not have sufficient permissions", { variant: 'error' });
                return;
            }
            const res = await axios.post(`${REST_API_END_POINT}project-task/delete-attachment`,{
                userId:user.user_id,attachments:checkedItems,projectId, taskId
            })
            if(res.data.status===1){
                enqueueSnackbar(res.data.message,{variant:'success'}) 
                setRefresh(!refresh)
                setCheckedItems([])
            }else{
                enqueueSnackbar(res.data.message,{variant:'error'}) 
            }
        } catch (error) {
            console.log(error)
        }
      }

    const handleAttachmentClick = (attachment) => {
        setSelectedAttachment(attachment);
        setOpen(true);
    };


    return (
        <>
            {canManage && checkedItems.length > 0 &&(
                  <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end',gap:2,m:2.5,  }}>
                  <Typography  variant='subtitle1'>{checkedItems.length} selected</Typography>
                    <Button onClick={()=>handleDelete()} className={classes.delete} size='small' variant='outlined'>
                         <Icon icon={trash2Outline} width={24} height={24} /> Delete
                         </Button>
                </Box>)}
                
                {processing ? (
                    <Box
                        display={'flex'}
                        justifyContent={'center'}
                        alignItems={'center'}
                        py={15}
                    >
                        <CircularProgress size={40} />
                        <Typography variant="body1" sx={{ ml: 2 }}>
                            Loading files...
                        </Typography>
                    </Box>
                ) : data?.length>0 ? (
            <Grid className={classes.gridContainer} container spacing={2} pb={4}>
                {data?.map((attachment, index) => {
                    const fileType = attachment.file_type?.toLowerCase();
                    const fileExtension = getAttachmentFileExtension(attachment);
                    const fileUrlLower = String(attachment.file_url || '').toLowerCase();
                    const isGoogleFormat = ['eps','ai', 'ps', 'psd','tif','tiff','indd','indt'].includes(fileExtension);
                    const isOfficeFile = ['xls','xlsx','doc','docx','ppt','pptx','csv'].includes(fileExtension);
                    const isPdf = fileExtension === 'pdf' || fileType === 'application/pdf' || fileUrlLower.includes('.pdf');

                    return (
                        <Grid key={attachment.ID} item xs={12} sm={4} >
                        <Box
                            className={classes.attachmentBox}
                            display="flex"
                            alignItems="center"
                            sx={{ position: 'relative' }}
                        >
                            <AttachmentDownloadIconButton
                                file={attachmentToFile(attachment)}
                                fileIndex={index}
                                hidden={!canDownloadFiles}
                                sx={{ position: 'absolute', top: 8, left: 8, zIndex: 12 }}
                            />
                            <Box onClick={() => handleAttachmentClick(attachment)} 
                                 style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                 <Tooltip title={isPdf ? "View PDF" : isGoogleFormat ? "View Design" : "View File"}>
                                                             <IconButton
                                                               onClick={() => {
                                                                handleAttachmentClick(attachment)
                                                               }}
                                                               aria-label="open file"
                                                                 sx={{ position:'absolute', 
                                                                 top: '50%',
                                                                 left: '50%',
                                                                 transform: 'translate(-50%, -50%)',
                                                                zIndex: 200,color:theme.palette.error.main, backgroundColor:theme.palette.text.primary }} 
                                                             >
                                                                {isOfficeFile ? (
                                                                  <Description style={{ fontSize: 40, color: 'green' }} />
                                                                ) : isPdf ? (
                                                                  <PictureAsPdf style={{ fontSize: 40, color: 'red' }} />
                                                                ) : isGoogleFormat ? (
                                                                  <Description style={{ fontSize: 40, color: 'blue' }} />
                                                                ) : (
                                                                  <Description style={{ fontSize: 40, color: 'grey' }} />
                                                                )}
                                                             </IconButton>
                                                           </Tooltip>
                          {isOfficeFile ? (
                            <iframe
                                src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(attachment?.file_url)}`}
                                title={''}
                                style={{ 
                                    width: '100%', 
                                    height: '100%',
                                    overflow: 'hidden',
                                    border: 'none'
                                }}
                                frameBorder="0"
                                scrolling="no"
                            />
                          ) : isGoogleFormat ? (
                            <iframe
                                src={`https://docs.google.com/gview?url=${encodeURIComponent(attachment?.file_url)}&embedded=true`}
                                title={''}
                                style={{ 
                                    width: '100%', 
                                    height: '100%',
                                    overflow: 'hidden',
                                    border: 'none'
                                }}
                                frameBorder="0"
                                scrolling="no"
                            />
                          ) : (
                            <iframe
                                src={attachment?.file_url}
                                title={''}
                                style={{ 
                                    width: '100%', 
                                    height: '100%',
                                    overflow: 'hidden',
                                    border: 'none'
                                }}
                                frameBorder="0"
                                scrolling="no"
                            />
                          )}
                                {/* <PictureAsPdf className={classes.attachmentIcon} /> */}
                                <Typography>{attachment?.name}</Typography>
                            </Box>
                            {canManage ? (
                                <Box className={classes.checkboxContainer}>
                                <Checkbox
                                    checked={checkedItems.includes(attachment.ID) ? true : false}
                                    onChange={() => handleCheckboxChange(attachment.ID)}
                                    color="primary"
                                />
                            </Box>
                            ) : null}
                        </Box>
                    </Grid>
                )})}
            </Grid>):(
                <Box
                display={'flex'}
                justifyContent={'center'}
                alignItems={'center'}
                py={15}
              >
                No files found
              </Box>
            )}
    <Box sx={{width:'100%'}}>


            <AttachmentPreviewDialog
                open={open}
                onClose={() => {
                    setOpen(false);
                    setSelectedAttachment(null);
                }}
                fileUrl={selectedAttachment?.file_url}
                file={attachmentToFile(selectedAttachment)}
                isClient={isClient}
                userType={userType}
                pdfLoading={pdfLoading}
                setPdfLoading={setPdfLoading}
                useOfficeViewer={useOfficeViewer}
                setUseOfficeViewer={setUseOfficeViewer}
            />
            {/* <DialogAnimate open={open} onClose={() => setOpen(false)}>
                {selectedAttachment && (() => {
                    const fileExtension = getAttachmentFileExtension(selectedAttachment);
                    const isGoogleFormat = ['eps','ai', 'ps', 'psd','tif','tiff','indd','indt'].includes(fileExtension);
                    const isOfficeFile = ['xls','xlsx','doc','docx','ppt','pptx','csv'].includes(fileExtension);
                    const isPdf = fileExtension === 'pdf' || selectedAttachment.file_type === 'application/pdf';

                    return (
                        <Card className={classes.dialogContent}>
                            <div style={{width:'90%', display:'flex',justifyContent:'flex-end', marginBottom: '15px',marginLeft:'25px' }}>
                                <Button
                                variant="outlined"
                                color="primary"
                                onClick={() => window.open(selectedAttachment.file_url, '_blank')}
                                disabled={!selectedAttachment.file_url}
                                startIcon={<Icon icon={filePdfIcon} width={20} height={20} />}
                                sx={{
                                    fontSize:'14px'
                                }}
                                >
                                {isPdf ? 'View PDF' : isGoogleFormat ? 'View Design' : 'View File'}
                                </Button>
                            </div>
                                {isOfficeFile ? (
                                    <iframe
                                        src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(selectedAttachment.file_url)}`}
                                        title={selectedAttachment.name}
                                        width="100%"
                                        height="500px"
                                        frameBorder="0"
                                    />
                                ) : isGoogleFormat ? (
                                    <iframe
                                        src={`https://docs.google.com/gview?url=${encodeURIComponent(selectedAttachment.file_url)}&embedded=true`}
                                        title={selectedAttachment.name}
                                        width="100%"
                                        height="500px"
                                        frameBorder="0"
                                    />
                                ) : (
                                    <embed src={selectedAttachment.file_url} type="application/pdf" width="100%" height="500px" />
                                )}
                        </Card>
                    );
                })()}
            </DialogAnimate> */}
                            </Box>
        </>
    );
}
